import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import {
  MapPin, Bed, Bath, Car, Maximize, Phone, MessageCircle,
  ChevronLeft, ChevronRight, Check, Calendar, Building2, Star
} from 'lucide-react';
import { companyInfo } from '../data/mock';

const PropertyModal = ({ property, isOpen, onClose }) => {
  const [currentImage, setCurrentImage] = useState(0);

  if (!property) return null;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-PE').format(price);
  };

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % property.images.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + property.images.length) % property.images.length);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent data-testid="property-modal" className="max-w-4xl max-h-[90vh] p-0 overflow-hidden bg-[#1A1A1A] rounded-none border border-[#D4A520]/20">
        <ScrollArea className="max-h-[90vh]">
          {/* Image Gallery */}
          <div className="relative h-72 sm:h-96">
            <img
              src={property.images[currentImage]}
              alt={property.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            
            {/* Navigation */}
            {property.images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  data-testid="modal-prev-img"
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 backdrop-blur-sm border border-[#D4A520]/30 flex items-center justify-center hover:bg-[#D4A520] hover:border-[#D4A520] transition-all"
                >
                  <ChevronLeft className="w-6 h-6 text-white" />
                </button>
                <button
                  onClick={nextImage}
                  data-testid="modal-next-img"
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 backdrop-blur-sm border border-[#D4A520]/30 flex items-center justify-center hover:bg-[#D4A520] hover:border-[#D4A520] transition-all"
                >
                  <ChevronRight className="w-6 h-6 text-white" />
                </button>
              </>
            )}

            {/* Thumbnails */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {property.images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentImage(idx)}
                  className={`w-12 h-12 overflow-hidden border transition-all ${
                    idx === currentImage ? 'border-[#D4A520] scale-110' : 'border-transparent opacity-70 hover:opacity-100'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>

            {/* Badge */}
            <div className="absolute top-4 left-4 flex gap-2">
              <Badge className="bg-[#D4A520] text-black px-4 py-1.5 text-sm rounded-none font-semibold uppercase tracking-wider">
                {property.type}
              </Badge>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 sm:p-8">
            <DialogHeader className="text-left mb-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <DialogTitle className="text-2xl sm:text-3xl font-bold text-white mb-2 tracking-tight">
                    {property.title}
                  </DialogTitle>
                  <div className="flex items-center gap-2 text-white/50">
                    <MapPin className="w-5 h-5 text-[#D4A520]" />
                    <span style={{ fontFamily: 'Outfit, sans-serif' }}>{property.location}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-[#D4A520]">
                    {property.currency} {formatPrice(property.price)}
                  </div>
                  {property.priceDollar && (
                    <div className="text-sm text-white/40" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      USD {formatPrice(property.priceDollar)}
                    </div>
                  )}
                </div>
              </div>
            </DialogHeader>

            {/* Quick Info */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-5 bg-[#0A0A0A] border border-[#D4A520]/10 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 border border-[#D4A520]/20 flex items-center justify-center">
                  <Bed className="w-5 h-5 text-[#D4A520]" />
                </div>
                <div>
                  <div className="text-xs text-white/40 uppercase tracking-wider" style={{ fontFamily: 'Outfit, sans-serif' }}>Dormitorios</div>
                  <div className="font-semibold text-white">{property.bedrooms}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 border border-[#D4A520]/20 flex items-center justify-center">
                  <Bath className="w-5 h-5 text-[#D4A520]" />
                </div>
                <div>
                  <div className="text-xs text-white/40 uppercase tracking-wider" style={{ fontFamily: 'Outfit, sans-serif' }}>Baños</div>
                  <div className="font-semibold text-white">{property.bathrooms}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 border border-[#D4A520]/20 flex items-center justify-center">
                  <Car className="w-5 h-5 text-[#D4A520]" />
                </div>
                <div>
                  <div className="text-xs text-white/40 uppercase tracking-wider" style={{ fontFamily: 'Outfit, sans-serif' }}>Estac.</div>
                  <div className="font-semibold text-white">{property.parking}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 border border-[#D4A520]/20 flex items-center justify-center">
                  <Maximize className="w-5 h-5 text-[#D4A520]" />
                </div>
                <div>
                  <div className="text-xs text-white/40 uppercase tracking-wider" style={{ fontFamily: 'Outfit, sans-serif' }}>Área</div>
                  <div className="font-semibold text-white">{property.area} m²</div>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mb-6">
              <h4 className="text-xl font-semibold text-white mb-3">Descripción</h4>
              <p className="text-white/50 leading-relaxed" style={{ fontFamily: 'Outfit, sans-serif' }}>{property.description}</p>
            </div>

            {/* Delivery Date */}
            {property.deliveryDate && (
              <div className="flex items-center gap-3 p-5 bg-[#D4A520]/5 border border-[#D4A520]/20 mb-6">
                <Calendar className="w-6 h-6 text-[#D4A520]" />
                <div>
                  <div className="text-xs text-white/40 uppercase tracking-wider" style={{ fontFamily: 'Outfit, sans-serif' }}>Fecha de Entrega</div>
                  <div className="font-semibold text-white">{property.deliveryDate}</div>
                </div>
              </div>
            )}

            {/* Highlights */}
            {property.highlights && (
              <div className="mb-6">
                <h4 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-[#D4A520]" />
                  Características Destacadas
                </h4>
                <div className="grid sm:grid-cols-2 gap-3">
                  {property.highlights.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-[#D4A520] flex-shrink-0 mt-0.5" />
                      <span className="text-white/60" style={{ fontFamily: 'Outfit, sans-serif' }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Distribution */}
            {property.distribution && (
              <div className="mb-6">
                <h4 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#D4A520]" />
                  Distribución
                </h4>
                <div className="space-y-4">
                  {Object.entries(property.distribution).map(([floor, rooms]) => (
                    <div key={floor} className="p-4 bg-[#0A0A0A] border border-[#D4A520]/10">
                      <h5 className="font-semibold text-[#D4A520] mb-2">{floor}</h5>
                      <div className="grid sm:grid-cols-2 gap-2">
                        {rooms.map((room, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-white/50">
                            <div className="w-1.5 h-1.5 bg-[#D4A520]" />
                            <span className="text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{room}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Finishes */}
            {property.finishes && (
              <div className="mb-6">
                <h4 className="text-xl font-semibold text-white mb-3">Acabados Premium</h4>
                <div className="grid sm:grid-cols-2 gap-3">
                  {property.finishes.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-[#D4A520] flex-shrink-0 mt-0.5" />
                      <span className="text-white/50 text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Nearby Places */}
            {property.nearbyPlaces && (
              <div className="mb-6">
                <h4 className="text-xl font-semibold text-white mb-3">Cerca de</h4>
                <div className="flex flex-wrap gap-2">
                  {property.nearbyPlaces.map((place, idx) => (
                    <Badge key={idx} variant="secondary" className="bg-[#0A0A0A] text-white/60 border border-[#D4A520]/10 px-3 py-1 rounded-none">
                      {place}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Bono Mi Vivienda */}
            {property.bonoMiVivienda && (
              <div className="p-5 bg-[#D4A520]/5 border border-[#D4A520]/20 mb-6">
                <p className="text-white/70 text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <strong className="text-[#D4A520]">¿Es tu primer inmueble?</strong> Te brindamos asesoría gratuita para que puedas obtener mediante crédito hipotecario el <strong className="text-white">Bono Mi Vivienda</strong>.
                </p>
              </div>
            )}

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-[#D4A520]/10">
              <a
                href={`https://wa.me/${companyInfo.whatsapp}?text=Hola, me interesa la propiedad: ${property.title}. Me gustaría agendar una visita.`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1"
              >
                <Button
                  data-testid="modal-whatsapp-btn"
                  className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white font-semibold py-6 rounded-none transition-all duration-300 hover:shadow-lg uppercase tracking-wider"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp
                </Button>
              </a>
              <a href={`tel:${companyInfo.phone1}`} className="flex-1">
                <Button
                  variant="outline"
                  data-testid="modal-call-btn"
                  className="w-full border border-[#D4A520]/30 text-[#D4A520] bg-transparent hover:bg-[#D4A520] hover:text-black font-semibold py-6 rounded-none transition-all duration-300 uppercase tracking-wider"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Llamar: {companyInfo.phone1}
                </Button>
              </a>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default PropertyModal;
